import { alpha } from "@mui/material/styles";
import { green, red, yellow } from "@mui/material/colors";
import { HalftimeDashboardRow, HalftimeApiResponse, PlayerRef, TeamRef, StatFields, PositionCategory } from "./types";

const GUARD_POSITIONS = new Set(["G"]);
const BIG_POSITIONS = new Set(["C", "C-F"]);

const POSITION_CATEGORY_OVERRIDES: Record<string, PositionCategory> = {
  "e1ef3d0d-0751-4dba-8838-84c0b1fb0ca1": "wing", // Vit Krejci
  "784758a1-b992-4bde-98ff-1a0fa6128d5c": "wing", // Sidy Cissoko
};

export const getPositionCategory = (position: string | null | undefined, playerId?: string): PositionCategory => {
  if (playerId && POSITION_CATEGORY_OVERRIDES[playerId]) return POSITION_CATEGORY_OVERRIDES[playerId];
  if (!position) return "wing";
  if (GUARD_POSITIONS.has(position)) return "guard";
  if (BIG_POSITIONS.has(position)) return "big";
  return "wing";
};

const CRASH_THRESHOLDS: Record<PositionCategory, { low: number; high: number }> = {
  guard: { low: 20, high: 40 },
  wing: { low: 40, high: 60 },
  big: { low: 60, high: 70 },
};

export const getCrashColor = (pct: number, positionCategory?: PositionCategory): string => {
  const { low, high } = CRASH_THRESHOLDS[positionCategory ?? "wing"];
  if (pct < low) return alpha(red[500], 0.2);
  if (pct > high) return alpha(green[500], 0.2);
  return alpha(yellow[700], 0.2);
};


export const getPaintTouchColor = (rate: number): string => {
  if (rate > 65) return alpha(green[500], 0.2);
  if (rate < 60) return alpha(red[500], 0.2);
  return alpha(yellow[700], 0.2);
};

export const getHitAheadColor = (rate: number): string => {
  if (rate > 20) return alpha(green[500], 0.2);
  if (rate < 15) return alpha(red[500], 0.2);
  return alpha(yellow[700], 0.2);
};

export const getPotentialAssistColor = (rate: number): string => {
  if (rate > 60) return alpha(green[500], 0.2);
  if (rate < 55) return alpha(red[500], 0.2);
  return alpha(yellow[700], 0.2);
};

export const formatShooting = (made: number | null, att: number | null): string => {
  if (made === null && att === null) return "";
  const m = made ?? 0;
  const a = att ?? 0;
  if (a === 0) return `${m}/${a}`;
  const pct = Math.round((m / a) * 100);
  return `${m}/${a} (${pct}%)`;
};

const emptyStats = (): StatFields => ({
  hitAheadPasses: null,
  potentialAssists: null,
  threeCsOpenAtt: null,
  threeCsOpenMade: null,
  threeCsLightAtt: null,
  threeCsLightMade: null,
  threeCsHeavyAtt: null,
  threeCsHeavyMade: null,
  threeNoncsOpenAtt: null,
  threeNoncsOpenMade: null,
  threeNoncsLightAtt: null,
  threeNoncsLightMade: null,
  threeNoncsHeavyAtt: null,
  threeNoncsHeavyMade: null,
  nonRimTwoOpenAtt: null,
  nonRimTwoOpenMade: null,
  nonRimTwoLightAtt: null,
  nonRimTwoLightMade: null,
  nonRimTwoHeavyAtt: null,
  nonRimTwoHeavyMade: null,
  rimOpenAtt: null,
  rimOpenMade: null,
  rimLightAtt: null,
  rimLightMade: null,
  rimHeavyAtt: null,
  rimHeavyMade: null,
  threePtCrashes: null,
  threePtCrashOpportunities: null,
  paintTouches: null,
  totalPossessions: null,
  totalFga: null,
});

export function transformHalftimeApiResponse(
  response: HalftimeApiResponse
): HalftimeDashboardRow[] {
  const { players, teams, markings } = response;

  const playerLookup: Record<string, PlayerRef> = {};
  players.forEach((p) => {
    playerLookup[p.id] = p;
  });

  const teamLookup: Record<string, TeamRef> = {};
  teams.forEach((t) => {
    teamLookup[t.id] = t;
  });

  const playerStats: Record<string, { teamId: string } & StatFields> = {};
  const teamStats: Record<string, StatFields> = {};

  // Build valid chance set (used for passing stats and paint touches)
  const validChanceIds = new Set<string>();
  if (markings.chances) {
    markings.chances.forEach(({ id, valid }) => {
      if (valid) {
        validChanceIds.add(id);
      }
    });
  }

  const chancePlayers = markings.chance_players;
  const shots = markings.shots;
  const possessions = markings.possessions;
  const touches = markings.touches;
  const passes = markings.passes;

  // Paint touches: count touches where player was in "key" or "ra" region with controlled possession
  if (touches) {
    touches.forEach(({ chanceId, playerId, offTeamId, regionsIn, startType }) => {
      if (!playerId || !offTeamId) return;
      if (validChanceIds.size && !validChanceIds.has(chanceId)) return;

      if (!regionsIn.some((r) => r === "key" || r === "ra")) return;

      if (startType === "orb" || startType === "other") return;

      if (!playerStats[playerId]) {
        playerStats[playerId] = { teamId: offTeamId, ...emptyStats() };
      }
      if (!teamStats[offTeamId]) {
        teamStats[offTeamId] = emptyStats();
      }

      playerStats[playerId].paintTouches = (playerStats[playerId].paintTouches ?? 0) + 1;
      teamStats[offTeamId].paintTouches = (teamStats[offTeamId].paintTouches ?? 0) + 1;
    });
  }

  if (passes) {
    passes.forEach(({ chanceId, passerId, offTeamId, passType, complete, assistOpp, distance }) => {
      if (!passerId) return;
      if (validChanceIds.size && !validChanceIds.has(chanceId)) return;

      if (!playerStats[passerId]) {
        playerStats[passerId] = { teamId: offTeamId, ...emptyStats() };
      }
      if (!teamStats[offTeamId]) {
        teamStats[offTeamId] = emptyStats();
      }

      if (passType === "pass ahead" && complete && distance > 15) {
        playerStats[passerId].hitAheadPasses = (playerStats[passerId].hitAheadPasses ?? 0) + 1;
        teamStats[offTeamId].hitAheadPasses = (teamStats[offTeamId].hitAheadPasses ?? 0) + 1;
      }

      if (assistOpp) {
        playerStats[passerId].potentialAssists = (playerStats[passerId].potentialAssists ?? 0) + 1;
        teamStats[offTeamId].potentialAssists = (teamStats[offTeamId].potentialAssists ?? 0) + 1;
      }
    });
  }

  if (possessions) {
    possessions.forEach(({ offTeamId, offPlayerIds }) => {
      if (!teamStats[offTeamId]) {
        teamStats[offTeamId] = emptyStats();
      }
      teamStats[offTeamId].totalPossessions = (teamStats[offTeamId].totalPossessions ?? 0) + 1;

      offPlayerIds.forEach((playerId) => {
        if (!playerStats[playerId]) {
          playerStats[playerId] = { teamId: offTeamId, ...emptyStats() };
        }
        playerStats[playerId].totalPossessions = (playerStats[playerId].totalPossessions ?? 0) + 1;
      });
    });
  }

  if (shots) {
    shots.forEach(({ shooterId, offTeamId, three, catchAndShoot, contestLevel, outcome, fouled, distance, shotType, complexShotType }) => {
      if (!shooterId) return;

      if (fouled && !outcome) return;

      if (!playerStats[shooterId]) {
        playerStats[shooterId] = { teamId: offTeamId, ...emptyStats() };
      }
      if (!teamStats[offTeamId]) {
        teamStats[offTeamId] = emptyStats();
      }

      const pStats = playerStats[shooterId];
      const tStats = teamStats[offTeamId];

      pStats.totalFga = (pStats.totalFga ?? 0) + 1;
      tStats.totalFga = (tStats.totalFga ?? 0) + 1;

      if (three && catchAndShoot) {
        if (contestLevel === "uncontested") {
          pStats.threeCsOpenAtt = (pStats.threeCsOpenAtt ?? 0) + 1;
          tStats.threeCsOpenAtt = (tStats.threeCsOpenAtt ?? 0) + 1;
          if (outcome) {
            pStats.threeCsOpenMade = (pStats.threeCsOpenMade ?? 0) + 1;
            tStats.threeCsOpenMade = (tStats.threeCsOpenMade ?? 0) + 1;
          }
        } else if (contestLevel === "lightly_contested") {
          pStats.threeCsLightAtt = (pStats.threeCsLightAtt ?? 0) + 1;
          tStats.threeCsLightAtt = (tStats.threeCsLightAtt ?? 0) + 1;
          if (outcome) {
            pStats.threeCsLightMade = (pStats.threeCsLightMade ?? 0) + 1;
            tStats.threeCsLightMade = (tStats.threeCsLightMade ?? 0) + 1;
          }
        } else if (contestLevel === "heavily_contested") {
          pStats.threeCsHeavyAtt = (pStats.threeCsHeavyAtt ?? 0) + 1;
          tStats.threeCsHeavyAtt = (tStats.threeCsHeavyAtt ?? 0) + 1;
          if (outcome) {
            pStats.threeCsHeavyMade = (pStats.threeCsHeavyMade ?? 0) + 1;
            tStats.threeCsHeavyMade = (tStats.threeCsHeavyMade ?? 0) + 1;
          }
        }
      }

      if (three && !catchAndShoot) {
        if (contestLevel === "uncontested") {
          pStats.threeNoncsOpenAtt = (pStats.threeNoncsOpenAtt ?? 0) + 1;
          tStats.threeNoncsOpenAtt = (tStats.threeNoncsOpenAtt ?? 0) + 1;
          if (outcome) {
            pStats.threeNoncsOpenMade = (pStats.threeNoncsOpenMade ?? 0) + 1;
            tStats.threeNoncsOpenMade = (tStats.threeNoncsOpenMade ?? 0) + 1;
          }
        } else if (contestLevel === "lightly_contested") {
          pStats.threeNoncsLightAtt = (pStats.threeNoncsLightAtt ?? 0) + 1;
          tStats.threeNoncsLightAtt = (tStats.threeNoncsLightAtt ?? 0) + 1;
          if (outcome) {
            pStats.threeNoncsLightMade = (pStats.threeNoncsLightMade ?? 0) + 1;
            tStats.threeNoncsLightMade = (tStats.threeNoncsLightMade ?? 0) + 1;
          }
        } else if (contestLevel === "heavily_contested") {
          pStats.threeNoncsHeavyAtt = (pStats.threeNoncsHeavyAtt ?? 0) + 1;
          tStats.threeNoncsHeavyAtt = (tStats.threeNoncsHeavyAtt ?? 0) + 1;
          if (outcome) {
            pStats.threeNoncsHeavyMade = (pStats.threeNoncsHeavyMade ?? 0) + 1;
            tStats.threeNoncsHeavyMade = (tStats.threeNoncsHeavyMade ?? 0) + 1;
          }
        }
      }

      const isRim = (shotType === "layup" && distance < 15) || complexShotType === "tip" || complexShotType === "lob";
      if (!three && isRim) {
        if (contestLevel === "uncontested") {
          pStats.rimOpenAtt = (pStats.rimOpenAtt ?? 0) + 1;
          tStats.rimOpenAtt = (tStats.rimOpenAtt ?? 0) + 1;
          if (outcome) {
            pStats.rimOpenMade = (pStats.rimOpenMade ?? 0) + 1;
            tStats.rimOpenMade = (tStats.rimOpenMade ?? 0) + 1;
          }
        } else if (contestLevel === "lightly_contested") {
          pStats.rimLightAtt = (pStats.rimLightAtt ?? 0) + 1;
          tStats.rimLightAtt = (tStats.rimLightAtt ?? 0) + 1;
          if (outcome) {
            pStats.rimLightMade = (pStats.rimLightMade ?? 0) + 1;
            tStats.rimLightMade = (tStats.rimLightMade ?? 0) + 1;
          }
        } else if (contestLevel === "heavily_contested") {
          pStats.rimHeavyAtt = (pStats.rimHeavyAtt ?? 0) + 1;
          tStats.rimHeavyAtt = (tStats.rimHeavyAtt ?? 0) + 1;
          if (outcome) {
            pStats.rimHeavyMade = (pStats.rimHeavyMade ?? 0) + 1;
            tStats.rimHeavyMade = (tStats.rimHeavyMade ?? 0) + 1;
          }
        }
      }

      if (!three && !isRim) {
        if (contestLevel === "uncontested") {
          pStats.nonRimTwoOpenAtt = (pStats.nonRimTwoOpenAtt ?? 0) + 1;
          tStats.nonRimTwoOpenAtt = (tStats.nonRimTwoOpenAtt ?? 0) + 1;
          if (outcome) {
            pStats.nonRimTwoOpenMade = (pStats.nonRimTwoOpenMade ?? 0) + 1;
            tStats.nonRimTwoOpenMade = (tStats.nonRimTwoOpenMade ?? 0) + 1;
          }
        } else if (contestLevel === "lightly_contested") {
          pStats.nonRimTwoLightAtt = (pStats.nonRimTwoLightAtt ?? 0) + 1;
          tStats.nonRimTwoLightAtt = (tStats.nonRimTwoLightAtt ?? 0) + 1;
          if (outcome) {
            pStats.nonRimTwoLightMade = (pStats.nonRimTwoLightMade ?? 0) + 1;
            tStats.nonRimTwoLightMade = (tStats.nonRimTwoLightMade ?? 0) + 1;
          }
        } else if (contestLevel === "heavily_contested") {
          pStats.nonRimTwoHeavyAtt = (pStats.nonRimTwoHeavyAtt ?? 0) + 1;
          tStats.nonRimTwoHeavyAtt = (tStats.nonRimTwoHeavyAtt ?? 0) + 1;
          if (outcome) {
            pStats.nonRimTwoHeavyMade = (pStats.nonRimTwoHeavyMade ?? 0) + 1;
            tStats.nonRimTwoHeavyMade = (tStats.nonRimTwoHeavyMade ?? 0) + 1;
          }
        }
      }
    });
  }

  const shooterByChance: Record<string, string> = {};
  const threeByChance: Record<string, boolean> = {};
  if (shots) {
    shots.forEach(({ chanceId, shooterId, three }) => {
      shooterByChance[chanceId] = shooterId;
      threeByChance[chanceId] = three;
    });
  }

  if (chancePlayers) {
    chancePlayers.forEach(({ chanceId, playerId, crashed, offense }) => {
      if (!playerId || !offense) return;

      const playerData = playerStats[playerId];
      if (!playerData) return;

      const { teamId } = playerData;
      if (!teamId) return;

      const isShooter = shooterByChance[chanceId] === playerId;
      const isThree = threeByChance[chanceId];

      // Only track crashes on 3-point attempts
      // For shooters: only count if they actually crashed (then count both opportunity and crash)
      // For non-shooters: count opportunity normally, and crash if they crashed
      if (crashed !== null && isThree) {
        if (isShooter) {
          if (crashed) {
            playerData.threePtCrashOpportunities = (playerData.threePtCrashOpportunities ?? 0) + 1;
            teamStats[teamId].threePtCrashOpportunities = (teamStats[teamId].threePtCrashOpportunities ?? 0) + 1;
            playerData.threePtCrashes = (playerData.threePtCrashes ?? 0) + 1;
            teamStats[teamId].threePtCrashes = (teamStats[teamId].threePtCrashes ?? 0) + 1;
          }
        } else {
          playerData.threePtCrashOpportunities = (playerData.threePtCrashOpportunities ?? 0) + 1;
          teamStats[teamId].threePtCrashOpportunities = (teamStats[teamId].threePtCrashOpportunities ?? 0) + 1;

          if (crashed) {
            playerData.threePtCrashes = (playerData.threePtCrashes ?? 0) + 1;
            teamStats[teamId].threePtCrashes = (teamStats[teamId].threePtCrashes ?? 0) + 1;
          }
        }
      }
    });
  }

  const rows: HalftimeDashboardRow[] = [];

  Object.entries(teamStats).forEach(([teamId, stats]) => {
    const teamName = teamLookup[teamId]?.name ?? "Team";
    rows.push({
      id: teamId,
      name: teamName,
      isTeam: true,
      teamId,
      ...stats,
    });
  });

  Object.entries(playerStats).forEach(([playerId, { teamId, ...stats }]) => {
    const playerInfo = playerLookup[playerId];
    const playerName = playerInfo
      ? `${playerInfo.firstName ?? ""} ${playerInfo.lastName ?? ""}`.trim()
      : "";

    rows.push({
      id: playerId,
      name: playerName,
      isTeam: false,
      teamId,
      positionCategory: getPositionCategory(playerInfo?.position, playerId),
      ...stats,
    });
  });

  return rows;
}
